import {
  Bell,
  Bot,
  Check,
  CircleHelp,
  Cloud,
  ExternalLink,
  FolderOpen,
  House,
  KeyRound,
  ListChecks,
  Lightbulb,
  LogIn,
  Moon,
  MonitorSmartphone,
  Save,
  Settings2,
  ShieldCheck,
  Smartphone,
  SquareTerminal,
  Sun,
  RefreshCw,
  UserRound,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import type {
  AutoContinuePolicy,
  Session,
  SessionManagementMode,
  SessionMode,
  Snapshot,
  View,
} from "./domain";
import type { CreateSessionInput, DesktopClient } from "@orni/domain-typescript";
import type {
  ConfigurableProviderId,
  DeviceSettings,
  OrniEvent,
  ProviderInventory,
  ProviderInventoryItem,
  ProviderHistoryCatalog,
} from "./sdk/types";
import type { RecoveryIncident } from "./sdk/recovery-types";
import { HomePage } from "./pages/HomePage";
import { SessionsPage } from "./pages/SessionsPage";
import { WorkPage } from "./pages/WorkPage";
import { SessionWorkspace } from "./pages/SessionWorkspace";
import {
  DesktopSessionShell,
  DesktopSettingsPanel,
} from "./pages/DesktopSessionShell";
import { NotificationsPage } from "./pages/NotificationsPage";
import { HelpPage } from "./pages/HelpPage";
import {
  AgentManagementPage,
  ProviderSettingsPage,
} from "./pages/SettingsPage";
import { ApiManagerPage } from "./pages/ApiManagerPage";
import { NotesPage } from "./pages/NotesPage";
import { notifyNotesChanged, QuickNotePopover } from "./components/Notes";
import { SessionCreateDialog } from "./components/SessionCreateDialog";
import { DesktopInitializationPanel } from "./components/DesktopInitializationPanel";
import { CommandDialog } from "./components/Ui";
import { PlatformNotices } from "./components/PlatformNotices";
import { LanguageSelector } from "./components/LanguageSelector";
import { RecoveryDialog } from "./components/RecoveryDialog";
import { DesktopLoginForm } from "./components/DesktopLoginForm";
import { AccountDialog } from "./components/AccountDialog";
import {
  loadPlatformBootstrap,
  type PlatformBootstrap,
} from "./platform-bootstrap";
import type { AuthPort, AuthState } from "./auth-port";
import type { RemoteDeviceSummary } from "@orni/sdk-typescript";
import type { SyncStatus } from "@orni/sdk-typescript";
import { applyTheme, readTheme, type ThemeMode } from "./theme";
import { sessionTitleFromPrompt } from "./text-input";
import { localizeError, useI18n } from "./i18n";
import {
  defaultDesktopAccessPreferences,
  mergeDeviceAccessPreferences,
  type DesktopAccessPreferences,
} from "./desktop-access-preferences";

interface AppProps {
  client: DesktopClient;
  auth: AuthPort;
}

type SessionTransportMode = "local" | "remote";
const desktopTransportStorageKey = "orni.desktop.session-transport";

function readSessionTransportMode(client: DesktopClient): SessionTransportMode {
  if (client.mode === "cloud") return "remote";
  try {
    return window.localStorage.getItem(desktopTransportStorageKey) === "remote"
      ? "remote"
      : "local";
  } catch {
    return "local";
  }
}

function launchingWorkSession(
  title: string,
  provider: Session["provider"],
  _managementMode: SessionManagementMode,
  t: ReturnType<typeof useI18n>["t"],
): Session {
  const now = new Date().toISOString();
  return {
    id: `launching_${crypto.randomUUID()}`,
    title,
    provider,
    managementMode: "native",
    state: "starting",
    workspaceLabel: t("profile.native"),
    workspacePath: "",
    activity: t("app.session.creatingCoordinator"),
    progress: 0,
    updated: t("app.time.justNow"),
    updatedAt: now,
    autoContinue: false,
    autoContinuePolicy: {
      enabled: true,
      recoveryEnabled: true,
      retryWindowSeconds: 180,
      retryForever: false,
      retryIntervalSeconds: 5,
      attemptsUsed: 0,
      recoveryHistory: [],
    },
    controller: "none",
    leg: [],
    launchingWork: true,
  };
}

function failedLaunchingSession(
  session: Session,
  message: string,
): Session {
  return {
    ...session,
    state: "error",
    activity: message,
    launchingWork: false,
    updatedAt: new Date().toISOString(),
  };
}

function mergePendingCreatedSession(
  current: Session,
  _pending: Session,
  keepStarting = true,
): Session {
  return {
    ...current,
    ...(keepStarting ? { state: "starting" as const, launchingWork: true } : {}),
  };
}

function resolvedCreatedSession(
  current: Session,
  pending: Session | undefined,
): { session: Session; confirmed: boolean } {
  if (!pending) {
    return { session: current, confirmed: true };
  }
  // `sessions.start` is deliberately accepted before the Provider process is
  // registered. During that window the authoritative snapshot still looks
  // stopped. Keep the creation shell until a real attempt appears instead of
  // sending the user to the inactive-session screen.
  const startStillPending =
    pending.launchingWork === true &&
    (current.state === "starting" || current.state === "stopped") &&
    !current.processAttemptId;
  if (startStillPending) {
    return {
      session: mergePendingCreatedSession(current, pending, true),
      confirmed: false,
    };
  }
  return { session: current, confirmed: true };
}

export function App({ client, auth }: AppProps) {
  const { locale, t } = useI18n();
  const initialParams = new URLSearchParams(window.location.search);
  const emptyInitPreview = initialParams.get("demo") === "empty-init";
  const initialSessionId = initialParams.get("session");
  const initialView = desktopViewFromRoute(initialParams.get("view"));
  const [snapshot, setSnapshot] = useState<Snapshot | null>(() =>
    emptyInitPreview ? emptyInitializationPreviewSnapshot() : null,
  );
  const [view, setView] = useState<View>(
    initialSessionId ? "sessions" : initialView,
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    initialSessionId,
  );
  const [selectedSessionDetail, setSelectedSessionDetail] =
    useState<Session | null>(null);
  const [sessionLoadError, setSessionLoadError] = useState<string | null>(null);
  const [returnSession, setReturnSession] = useState<Session | null>(null);
  const [newSessionMode, setNewSessionMode] =
    useState<SessionManagementMode | null>(null);
  const [newSessionProvider, setNewSessionProvider] =
    useState<ConfigurableProviderId | null>(null);
  const [newSessionSource, setNewSessionSource] = useState<"new" | "import">(
    "new",
  );
  const [allowMultipleImports, setAllowMultipleImports] = useState(false);
  const [apiManagerCli, setApiManagerCli] = useState<string>("codex");
  const [newSessionSeed, setNewSessionSeed] = useState<{
    title?: string;
    noteId?: string;
  } | null>(null);
  const [sessionInsertRequest, setSessionInsertRequest] = useState<{
    sessionId: string;
    text: string;
    nonce: number;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [productDialog, setProductDialog] = useState<"mobile" | "login" | null>(
    null,
  );
  const [authState, setAuthState] = useState<AuthState>(auth.state);
  const [authInitialized, setAuthInitialized] = useState(
    client.mode === "cloud",
  );
  const [sessionTransportMode, setSessionTransportMode] =
    useState<SessionTransportMode>(() => readSessionTransportMode(client));
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<"settings" | "api">("settings");
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [detailRevision, setDetailRevision] = useState(0);
  const [deviceSettings, setDeviceSettings] = useState<DeviceSettings | null>(null);
  const [accessPreferences, setAccessPreferences] =
    useState<DesktopAccessPreferences>(() => defaultDesktopAccessPreferences());
  const [providerInventory, setProviderInventory] = useState<ProviderInventory | null>(null);
  const [providerInventoryLoading, setProviderInventoryLoading] = useState(true);
  const [cloudDevices, setCloudDevices] = useState<RemoteDeviceSummary[]>([]);
  const [cloudTargetDeviceId, setCloudTargetDeviceId] = useState<string | null>(
    client.getCloudTargetDevice(),
  );
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [recoveryIncidents, setRecoveryIncidents] = useState<RecoveryIncident[]>([]);
  const [recoveryDialogDeferred, setRecoveryDialogDeferred] = useState(false);
  // Recovery remains available to a future explicit entry point, but an
  // application restart must never interrupt the user with this dialog.
  const recoveryDialogEnabled = false;
  const [theme, setTheme] = useState<ThemeMode>(readTheme);
  const [platform, setPlatform] = useState<PlatformBootstrap>({
    announcements: [],
    menus: [],
  });
  const createOperationSequence = useRef(0);
  const pendingCreatedSessionsRef = useRef(new Map<string, Session>());
  const snapshotRefreshTimer = useRef<number | null>(null);
  const detailRefreshTimer = useRef<number | null>(null);
  const snapshotRefreshInFlight = useRef(false);
  const snapshotRefreshQueued = useRef(false);
  // Detail reads are deliberately allowed to overlap with the fleet snapshot
  // refresh, but an older response must never replace a newer one.
  const detailRequestGenerationRef = useRef(0);
  const selectedSessionIdRef = useRef<string | null>(null);
  const selectedSessionDetailRef = useRef<Session | null>(null);
  const cloudTargetDeviceIdRef = useRef(cloudTargetDeviceId);
  selectedSessionIdRef.current = selectedSessionId;
  selectedSessionDetailRef.current = selectedSessionDetail;
  cloudTargetDeviceIdRef.current = cloudTargetDeviceId;

  useEffect(() => {
    if (!deviceSettings) return;
    setAccessPreferences((current) =>
      mergeDeviceAccessPreferences(current, deviceSettings),
    );
  }, [deviceSettings]);

  const selectCloudTargetDevice = useCallback((deviceId: string | null) => {
    const next = deviceId?.trim() || null;
    client.setCloudTargetDevice(next);
    // Selecting the already-active computer is a no-op. Clearing the
    // computer-owned state here without changing the dependency below would
    // leave the Provider list empty until a full page reload.
    if (cloudTargetDeviceIdRef.current === next) return;
    cloudTargetDeviceIdRef.current = next;
    setCloudTargetDeviceId(next);
    // Only Cloud Web changes its computer-owned settings context. Desktop
    // settings, Provider discovery and API configuration always belong to the
    // local Daemon, even while remote Sessions remain visible.
    if (client.mode === "cloud") {
      setDeviceSettings(null);
      setProviderInventory(null);
    }
    setRecoveryIncidents([]);
    setDetailRevision((value) => value + 1);
  }, [client]);
  const changeSessionTransportMode = useCallback((mode: SessionTransportMode) => {
    if (client.mode === "cloud") return;
    if (mode === "remote" && authState !== "authenticated") {
      setProductDialog("login");
      return;
    }
    setSessionTransportMode(mode);
    try {
      window.localStorage.setItem(desktopTransportStorageKey, mode);
    } catch {
      // Restricted WebViews can disable storage; the in-memory choice remains.
    }
    if (mode === "local") selectCloudTargetDevice(null);
  }, [authState, client.mode, selectCloudTargetDevice]);

  const refresh = useCallback(async () => {
    if (emptyInitPreview) return;
    if (snapshotRefreshInFlight.current) {
      snapshotRefreshQueued.current = true;
      return;
    }
    snapshotRefreshInFlight.current = true;
    try {
      setSnapshot(await client.snapshot());
    } finally {
      snapshotRefreshInFlight.current = false;
      if (
        snapshotRefreshQueued.current &&
        snapshotRefreshTimer.current === null
      ) {
        snapshotRefreshQueued.current = false;
        snapshotRefreshTimer.current = window.setTimeout(() => {
          snapshotRefreshTimer.current = null;
          void refresh();
        }, 500);
      }
    }
  }, [client, emptyInitPreview]);
  const scheduleRefresh = useCallback(
    (event: OrniEvent) => {
      const selectedDetail = selectedSessionDetailRef.current;
      const relatedSessionChanged =
        event.type === "session.detail.invalidated" &&
        Boolean(
          selectedDetail?.workContext?.sessionHistory.some(
            (item) => item.sessionId === event.sessionId,
          ),
        );
      const selectedSessionChanged =
        (event.type === "session.detail.invalidated" &&
          event.sessionId === selectedSessionIdRef.current) ||
        (event.type === "terminal.appended" &&
          event.sessionId === selectedSessionIdRef.current) ||
        (event.type === "session.updated" &&
          event.session.id === selectedSessionIdRef.current) ||
        relatedSessionChanged ||
        (event.type === "snapshot.invalidated" &&
          Boolean(selectedSessionIdRef.current));
      if (selectedSessionChanged) {
        if (detailRefreshTimer.current === null) {
          detailRefreshTimer.current = window.setTimeout(() => {
            detailRefreshTimer.current = null;
            setDetailRevision((value) => value + 1);
          }, detailRefreshDelay(event));
        }
      }
      const delay = snapshotRefreshDelay(event);
      if (delay !== null && snapshotRefreshTimer.current === null) {
        snapshotRefreshTimer.current = window.setTimeout(() => {
          snapshotRefreshTimer.current = null;
          void refresh();
        }, delay);
      }
    },
    [refresh],
  );
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  useEffect(() => {
    const unsubscribe = auth.subscribe(setAuthState);
    if (client.mode !== "cloud") {
      let active = true;
      void auth.initialize().finally(() => {
        if (active) setAuthInitialized(true);
      });
      return () => {
        active = false;
        unsubscribe();
      };
    }
    return unsubscribe;
  }, [auth, client.mode]);
  useEffect(() => {
    if (authState !== "authenticated") {
      setAccountEmail(null);
      return;
    }
    let active = true;
    void auth.accountInfo()
      .then((account) => {
        if (active) setAccountEmail(account.email);
      })
      .catch(() => {
        if (active) setAccountEmail(null);
      });
    return () => {
      active = false;
    };
  }, [auth, authState]);
  useEffect(() => {
    const authenticated = client.mode === "cloud" || (
      authInitialized && authState === "authenticated"
    );
    const remoteSessionsEnabled = client.mode === "cloud" || (
      sessionTransportMode === "remote" &&
      authenticated
    );
    client.setCloudAccessEnabled(authenticated);
    client.setRemoteSessionsEnabled(remoteSessionsEnabled);
    if (
      client.mode === "local" &&
      sessionTransportMode === "remote" &&
      authInitialized &&
      authState !== "authenticated"
    ) {
      setSessionTransportMode("local");
      client.setCloudTargetDevice(null);
      try {
        window.localStorage.setItem(desktopTransportStorageKey, "local");
      } catch {
        // Keep the safe local fallback even when persistence is unavailable.
      }
    }
  }, [authInitialized, authState, client, sessionTransportMode]);
  useEffect(() => {
    if (
      (client.mode !== "cloud" && sessionTransportMode !== "remote") ||
      cloudTargetDeviceId ||
      !cloudDevices.length
    ) return;
    const target = cloudDevices.find((device) => device.online) ?? cloudDevices[0];
    selectCloudTargetDevice(target.deviceId);
  }, [client.mode, cloudDevices, cloudTargetDeviceId, selectCloudTargetDevice, sessionTransportMode]);
  useEffect(() => {
    if (emptyInitPreview) {
      setCloudDevices([]);
      return;
    }
    if (authState !== "authenticated" && sessionTransportMode !== "remote" && client.mode !== "cloud") {
      setCloudDevices([]);
      return;
    }
    let active = true;
    let timer: number | null = null;
    const loadDevices = async () => {
      try {
        const devices = await client.listCloudDevices();
        if (!active) return;
        setCloudDevices((current) =>
          deviceListFingerprint(current) === deviceListFingerprint(devices)
            ? current
            : devices,
        );
      } catch {
        // Keep the last known fleet visible during a transient Cloud outage.
      } finally {
        if (active) timer = window.setTimeout(() => void loadDevices(), 10_000);
      }
    };
    void loadDevices();
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [authState, client, emptyInitPreview, sessionTransportMode]);
  useEffect(() => {
    if (emptyInitPreview) return;
    void refresh();
    const unsubscribe = client.subscribe(scheduleRefresh);
    return () => {
      unsubscribe();
      if (snapshotRefreshTimer.current !== null)
        window.clearTimeout(snapshotRefreshTimer.current);
      if (detailRefreshTimer.current !== null)
        window.clearTimeout(detailRefreshTimer.current);
      snapshotRefreshTimer.current = null;
      detailRefreshTimer.current = null;
    };
  }, [client, emptyInitPreview, refresh, scheduleRefresh]);
  useEffect(() => {
    if (emptyInitPreview) return;
    void refresh();
    if (selectedSessionIdRef.current)
      setDetailRevision((value) => value + 1);
  }, [emptyInitPreview, locale, refresh]);
  useEffect(() => {
    const restoreRoute = () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session");
      setReturnSession(null);
      setSessionLoadError(null);
      setSelectedSessionDetail(null);
      setSelectedSessionId(sessionId);
      setView(
        sessionId
          ? "sessions"
          : desktopViewFromRoute(params.get("view")),
      );
    };
    window.addEventListener("popstate", restoreRoute);
    return () => window.removeEventListener("popstate", restoreRoute);
  }, []);
  useEffect(() => {
    if (
      view !== "sessions" ||
      !selectedSessionId ||
      selectedSessionId.startsWith("launching_") ||
      !selectedSessionDetail ||
      !["starting", "running", "waiting"].includes(
        selectedSessionDetail.state,
      )
    ) {
      return;
    }
    // Cloud events are an invalidation hint, not the only delivery path. A
    // selected Session must continue reconciling its authoritative detail
    // projection while an SSE connection is reconnecting.
    // Keep this scoped to the open session so the fleet list is not polled at
    // the same cadence and the UI does not flash between stale snapshots.
    // A detail read includes the Cloud Session projection. Polling faster than
    // that request can finish
    // creates overlapping reads, which makes a starting Session look like it
    // is flickering or unavailable. Cloud events still invalidate immediately;
    // this timer is only the bounded fallback while a process becomes ready.
    const refreshIntervalMs = client.mode === "cloud"
      ? selectedSessionDetail.state === "starting" ? 1_500 : 5_000
      : selectedSessionDetail.state === "starting" ? 1_000 : 10_000;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible")
        setDetailRevision((value) => value + 1);
    }, refreshIntervalMs);
    return () => window.clearInterval(timer);
  }, [client, selectedSessionDetail?.state, selectedSessionId, view]);
  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;
    const loadComputerState = async () => {
      setProviderInventoryLoading(true);
      const [settingsResult, providersResult] = await Promise.allSettled([
        client.getDeviceSettings(),
        client.getProviderInventory(),
      ]);
      if (cancelled) return;
      if (settingsResult.status === "fulfilled") {
        setDeviceSettings(emptyInitPreview
          ? { ...settingsResult.value, onboardingCompleted: false }
          : settingsResult.value);
        if (client.mode === "cloud") {
          setSnapshot((current) => current ? {
            ...current,
            preferences: {
              ...current.preferences,
              sessionRoot: settingsResult.value.sessionRoot,
            },
          } : current);
        }
      }
      if (providersResult.status === "fulfilled") {
        setProviderInventory(providersResult.value);
        setProviderInventoryLoading(false);
      }
      // A Cloud target may be reconnecting or an older Daemon may still be
      // finishing its protocol upgrade. Do not turn a transient command
      // failure into a permanent empty Provider list on this page.
      if (
        settingsResult.status === "rejected" ||
        providersResult.status === "rejected"
      ) {
        retryTimer = window.setTimeout(() => {
          retryTimer = null;
          void loadComputerState();
        }, 3_000);
      }
    };
    void loadComputerState();
    void client
      .listRecoveryIncidents()
      .then((incidents) => {
        if (!cancelled) setRecoveryIncidents(incidents);
      })
      .catch(() => undefined);
    void loadPlatformBootstrap().then((bootstrap) => {
      if (!cancelled) setPlatform(bootstrap);
    });
    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [client, cloudTargetDeviceId, emptyInitPreview]);
  const configuredNativeAgentCount = providerInventory?.providers.filter(
    (provider) => !provider.candidate && provider.supportsNative && provider.enabled && provider.installed,
  ).length ?? 0;
  // This is a durable setup marker, not a route redirect. Keep the shell
  // usable while it is pending and render the setup controls in the empty
  // right pane so Agent discovery and computer settings are immediately usable.
  const initializationRequired = Boolean(
    !deviceSettings ||
    (providerInventory === null && providerInventoryLoading) ||
    deviceSettings && (
      !deviceSettings.onboardingCompleted ||
      (!providerInventoryLoading && configuredNativeAgentCount === 0)
    ),
  );
  const completeInitialization = useCallback(async () => {
    if (!deviceSettings || configuredNativeAgentCount === 0) return;
    const settings = await client.completeOnboarding({
      sessionRoot: deviceSettings.sessionRoot,
      expectedRevision: deviceSettings.revision,
    });
    setDeviceSettings(settings);
  }, [client, configuredNativeAgentCount, deviceSettings]);
  const selectedSummary =
    snapshot?.sessions.find((item) => item.id === selectedSessionId) ?? null;
  useEffect(() => {
    if (!selectedSessionId) {
      setSelectedSessionDetail(null);
      setSessionLoadError(null);
      return;
    }
    // A durable Session can be returned before the list snapshot observes its
    // creation event. Its detail endpoint is already authoritative, so do not
    // leave the local "creating" shell waiting on an unrelated list refresh.
    if (selectedSessionId.startsWith("launching_")) return;
    let cancelled = false;
    const requestGeneration = ++detailRequestGenerationRef.current;
    let retryTimer: number | null = null;
    setSessionLoadError(null);
    void client
      .getSession(selectedSessionId)
      .then((session) => {
        if (cancelled || requestGeneration !== detailRequestGenerationRef.current) return;
        const pending = pendingCreatedSessionsRef.current.get(session.id);
        if (!pending) {
          setSelectedSessionDetail(session);
          return;
        }
        const startStillPending =
          pending.launchingWork === true &&
          (session.state === "starting" || session.state === "stopped") &&
          !session.processAttemptId;
        if (!startStillPending) {
          pendingCreatedSessionsRef.current.delete(session.id);
          setSelectedSessionDetail(session);
          return;
        }
        setSelectedSessionDetail(
          mergePendingCreatedSession(session, pending, startStillPending),
        );
      })
      .catch((error: unknown) => {
        if (!cancelled && requestGeneration === detailRequestGenerationRef.current) {
          // Cloud creates the durable Session before its projection and
          // process attempt become visible. During that short window the
          // detail read can legitimately miss or time out. Keep the creating
          // shell visible and retry instead of flashing a terminal error.
          const pending = pendingCreatedSessionsRef.current.get(selectedSessionId);
          if (pending) {
            setSelectedSessionDetail((current) => current ?? pending);
            setSessionLoadError(null);
            retryTimer = window.setTimeout(() => {
              retryTimer = null;
              if (!cancelled) setDetailRevision((value) => value + 1);
            }, 800);
            return;
          }
          setSelectedSessionDetail(null);
          setSessionLoadError(
            localizeError(error, t, "app.session.detailLoadFailed"),
          );
        }
      });
    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [
    client,
    selectedSessionId,
    selectedSummary?.updatedAt,
    selectedSummary?.state,
    selectedSummary?.activity,
    detailRevision,
    t,
  ]);

  // Keep the historical error gate for the legacy route contract, but let the
  // list projection mount the Native Terminal while an optional detail read is
  // catching up. A transient detail failure must not make an existing Session
  // look unopened.
  const selectedSession = sessionLoadError
    ? null
    : selectedSessionDetail?.id === selectedSessionId
      ? selectedSessionDetail
      : selectedSummary;
  const shellSession = selectedSession ?? selectedSummary;
  const openSession = (id: string, requestedMode?: SessionMode, insertText?: string) => {
    const target = snapshot?.sessions.find((session) => session.id === id);
    updateBrowserRoute(
      { sessionId: id, mode: requestedMode ?? defaultSessionMode(target, client.mode) },
      "push",
    );
    setReturnSession(null);
    setSessionLoadError(null);
    setSelectedSessionDetail(null);
    setSelectedSessionId(id);
    setSessionInsertRequest(insertText
      ? { sessionId: id, text: insertText, nonce: Date.now() }
      : null);
    setView("sessions");
  };
  const closeSession = () => {
    if (returnSession) {
      updateBrowserRoute(
        {
          sessionId: returnSession.id,
          mode: defaultSessionMode(returnSession, client.mode),
        },
        "push",
      );
      setSessionLoadError(null);
      setSelectedSessionDetail(returnSession);
      setSelectedSessionId(returnSession.id);
      setReturnSession(null);
      return;
    }
    setSessionLoadError(null);
    setSelectedSessionId(null);
    setSelectedSessionDetail(null);
    setSessionInsertRequest(null);
    setView("sessions");
    updateBrowserRoute({ view: "sessions" }, "push");
  };
  const openView = (next: View) => {
    updateBrowserRoute({ view: next }, "push");
    setView(next);
    setReturnSession(null);
    setSelectedSessionId(null);
    setSelectedSessionDetail(null);
    setNewSessionMode(null);
    setNewSessionProvider(null);
    setNewSessionSource("new");
    setNewSessionSeed(null);
    setAllowMultipleImports(false);
  };
  const showNotice = (text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice(null), 2600);
  };
  const emptyPreviewHistoryCatalog = (): ProviderHistoryCatalog => ({
    conversationCount: 0,
    providers: [],
    nextCursor: null,
    nextCursorToken: null,
    totalConversationCount: 0,
    complete: true,
  });
  const loadSyncStatus = useCallback(async () => {
    if (emptyInitPreview) return;
    setSyncStatusLoading(true);
    try {
      setSyncStatus(await client.getSyncStatus());
    } catch {
      // An unavailable transport is a real state, not an empty queue. Keep
      // the last counters while marking the authority offline for the UI.
      setSyncStatus((current) => current ? { ...current, state: "offline" } : null);
    } finally {
      setSyncStatusLoading(false);
    }
  }, [client, emptyInitPreview]);
  useEffect(() => {
    if (emptyInitPreview) return;
    void loadSyncStatus();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadSyncStatus();
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [emptyInitPreview, loadSyncStatus]);
  const run = (work: Promise<unknown>, success?: string) => {
    void work
      .then(() => {
        if (success) showNotice(success);
      })
      .catch((error: unknown) =>
        showNotice(localizeError(error, t, "app.operationFailed")),
      );
  };
  const updateSessionRoot = async (path: string) => {
    await client.setSessionRoot(path);
    setDeviceSettings(await client.getDeviceSettings());
    await refresh();
  };
  const saveInitializationComputerName = async (computerName: string) => {
    const settings = await client.updateDesktopSettings({
      computerName,
      expectedRevision: deviceSettings?.revision,
    });
    setDeviceSettings(emptyInitPreview ? { ...settings, onboardingCompleted: false } : settings);
    setAccessPreferences((current) => mergeDeviceAccessPreferences({ ...current, computerName }, settings));
  };
  const saveInitializationSessionRoot = async (sessionRoot: string) => {
    await client.setSessionRoot(sessionRoot);
    const settings = await client.getDeviceSettings();
    setDeviceSettings(emptyInitPreview ? { ...settings, onboardingCompleted: false } : settings);
    setSnapshot((current) => current ? { ...current, preferences: { ...current.preferences, sessionRoot } } : current);
  };
  const refreshInitializationInventory = async () => {
    setProviderInventory(await client.refreshProviderInventory());
  };
  const toggleInitializationProvider = async (provider: ProviderInventoryItem) => {
    setProviderInventory(await client.updateProviderSettings(provider.providerId, {
      enabled: !provider.enabled,
      executablePath: provider.configuredExecutablePath ?? provider.executablePath,
      homeDirectory: provider.configuredHomeDirectory ?? provider.homeDirectory,
      expectedRevision: provider.revision,
    }));
  };
  const updateAccessPreferences = async (
    next: DesktopAccessPreferences,
  ) => {
    const settings = await client.updateDesktopSettings({
      ...next,
      expectedRevision: deviceSettings?.revision,
    });
    setDeviceSettings(settings);
    setAccessPreferences(() => mergeDeviceAccessPreferences(next, settings));
  };
  const createWork = async (
    prompt: string,
    options: {
      provider: Session["provider"];
      managementMode: SessionManagementMode;
    },
  ) => {
    const createOperation = ++createOperationSequence.current;
    const isCurrentCreate = () =>
      createOperationSequence.current === createOperation;
    const title = sessionTitleFromPrompt(prompt);
    const launching = launchingWorkSession(
      title,
      options.provider,
      options.managementMode,
      t,
    );
    selectedSessionIdRef.current = launching.id;
    updateBrowserRoute({ view: "sessions" }, "replace");
    setSelectedSessionDetail(launching);
    setSelectedSessionId(launching.id);
    setView("sessions");
    let durableSessionId = "";
    try {
      const selectCreatedSession = (created: Session) => {
        durableSessionId = created.id;
        pendingCreatedSessionsRef.current.set(created.id, created);
        if (!isCurrentCreate()) return;
        selectedSessionIdRef.current = created.id;
        setSelectedSessionDetail(created);
        setSelectedSessionId(created.id);
        setView("sessions");
        updateBrowserRoute(
          {
            sessionId: created.id,
            mode: defaultSessionMode(created, client.mode),
          },
          "replace",
        );
      };
      const session = await client.createSession(
        {
          title,
          provider: options.provider,
          managementMode: "native",
          permissionMode: accessPreferences.defaultPermissionMode,
        },
        selectCreatedSession,
      );
      const pending = pendingCreatedSessionsRef.current.get(session.id);
      const resolved = resolvedCreatedSession(session, pending);
      if (resolved.confirmed) pendingCreatedSessionsRef.current.delete(session.id);
      if (isCurrentCreate() && selectedSessionIdRef.current === session.id) {
        setSelectedSessionDetail(resolved.session);
        setSelectedSessionId(session.id);
        updateBrowserRoute(
          {
            sessionId: session.id,
            mode: defaultSessionMode(session, client.mode),
          },
          "replace",
        );
        showNotice(t("app.session.coordinatorReady"));
      }
    } catch (error) {
      const createdSessionId = durableSessionId;
      if (createdSessionId) {
        const pending = pendingCreatedSessionsRef.current.get(createdSessionId);
        const latest = await client.getSession(createdSessionId).catch(() => pending);
        if (latest) {
          const resolved = resolvedCreatedSession(latest, pending);
          if (resolved.confirmed)
            pendingCreatedSessionsRef.current.delete(createdSessionId);
          if (
            isCurrentCreate() &&
            selectedSessionIdRef.current === createdSessionId
          ) {
            setSelectedSessionDetail(resolved.session);
          }
        }
        if (
          isCurrentCreate() &&
          selectedSessionIdRef.current === createdSessionId
        ) {
          setSelectedSessionId(createdSessionId);
          setView("sessions");
          updateBrowserRoute(
            { sessionId: createdSessionId, mode: defaultSessionMode(pending, client.mode) },
            "replace",
          );
          showNotice(localizeError(error, t, "app.session.createFailed"));
        }
        return;
      }
      if (isCurrentCreate()) {
        const message = localizeError(error, t, "app.session.createFailed");
        setSelectedSessionDetail(failedLaunchingSession(launching, message));
        setSelectedSessionId(launching.id);
        setView("sessions");
        updateBrowserRoute({ view: "sessions" }, "replace");
        showNotice(message);
      }
      throw error;
    }
  };
  const createSession = async (input: CreateSessionInput) => {
    const createOperation = ++createOperationSequence.current;
    const isCurrentCreate = () =>
      createOperationSequence.current === createOperation;
    const noteAssociation = newSessionSeed?.noteId
      ? { noteId: newSessionSeed.noteId }
      : null;
    const associateCreatedSession = async (sessionId: string) => {
      if (!noteAssociation) return;
      try {
        const current = await client.getNote(noteAssociation.noteId);
        await client.updateNote(current.note.id, {
          name: current.note.name,
          sessionId,
          expectedRevision: current.note.revision,
        });
        notifyNotesChanged();
      } finally {
        if (isCurrentCreate()) setNewSessionSeed(null);
      }
    };
    // Enter the session immediately with a local shell. The Daemon command
    // may take several seconds to return the durable id, but the user should
    // see an honest "connecting" state instead of a modal that appears stuck.
    // The create field is a title only. Provider input starts in the real PTY.
    const title = input.title.trim() || input.provider;
    const effectiveInput: CreateSessionInput = {
      ...input,
      title,
      initialInstruction: undefined,
    };
    const launching = launchingWorkSession(
      title,
      input.provider,
      input.managementMode ?? "native",
      t,
    );
    if (input.importExisting) {
      launching.workspacePath = input.workspaceRoot ?? "";
      launching.activity = t("app.session.creatingProject");
    }
    selectedSessionIdRef.current = launching.id;
    setNewSessionMode(null);
    setNewSessionProvider(null);
    setNewSessionSource("new");
    setSelectedSessionDetail(launching);
    setSelectedSessionId(launching.id);
    setView("sessions");
    updateBrowserRoute({ view: "sessions" }, "replace");
    let durableSessionId = "";
    try {
      const session = await client.createSession(effectiveInput, (created) => {
        durableSessionId = created.id;
        pendingCreatedSessionsRef.current.set(created.id, created);
        if (!isCurrentCreate()) return;
        selectedSessionIdRef.current = created.id;
        setNewSessionMode(null);
        setNewSessionSource("new");
        setSelectedSessionDetail(created);
        setSelectedSessionId(created.id);
        setView("sessions");
        updateBrowserRoute(
          {
            sessionId: created.id,
            mode: "terminal",
          },
          "replace",
        );
      });
      const pending = pendingCreatedSessionsRef.current.get(session.id);
      const resolved = resolvedCreatedSession(session, pending);
      if (resolved.confirmed) pendingCreatedSessionsRef.current.delete(session.id);
      const associationFailed = await associateCreatedSession(session.id)
        .then(() => false)
        .catch((cause) => {
          if (isCurrentCreate())
            showNotice(localizeError(cause, t, "notes.associationFailed"));
          return true;
        });
      if (isCurrentCreate() && selectedSessionIdRef.current === session.id) {
        setNewSessionMode(null);
        setNewSessionSource("new");
        setSelectedSessionDetail(resolved.session);
        setSelectedSessionId(session.id);
        setView("sessions");
        if (input.importExisting) {
          updateBrowserRoute(
            { sessionId: session.id, mode: "terminal" },
            "replace",
          );
          if (!associationFailed) showNotice(t("app.session.importReading"));
        } else {
          updateBrowserRoute(
            { sessionId: session.id, mode: defaultSessionMode(session, client.mode) },
            "replace",
          );
          if (!associationFailed) showNotice(t("app.session.preparingProvider"));
        }
      }
    } catch (error) {
      const createdSessionId = durableSessionId;
      if (createdSessionId) {
        const pending = pendingCreatedSessionsRef.current.get(createdSessionId);
        const latest = await client.getSession(createdSessionId).catch(() => pending);
        if (latest) {
          const resolved = resolvedCreatedSession(latest, pending);
          if (resolved.confirmed)
            pendingCreatedSessionsRef.current.delete(createdSessionId);
          if (
            isCurrentCreate() &&
            selectedSessionIdRef.current === createdSessionId
          ) {
            setSelectedSessionDetail(resolved.session);
          }
        }
        await associateCreatedSession(createdSessionId).catch((cause) => {
          if (isCurrentCreate())
            showNotice(localizeError(cause, t, "notes.associationFailed"));
        });
        if (
          isCurrentCreate() &&
          selectedSessionIdRef.current === createdSessionId
        ) {
          setSelectedSessionId(createdSessionId);
          setNewSessionMode(null);
          setNewSessionSource("new");
          setView("sessions");
          updateBrowserRoute(
            {
              sessionId: createdSessionId,
              mode: "terminal",
            },
            "replace",
          );
          showNotice(localizeError(error, t, "app.session.createFailed"));
        }
        return;
      }
      if (isCurrentCreate()) {
        const message = localizeError(error, t, "app.session.createFailed");
        setSelectedSessionDetail(failedLaunchingSession(launching, message));
        setSelectedSessionId(launching.id);
        setView("sessions");
        updateBrowserRoute({ view: "sessions" }, "replace");
        showNotice(message);
      }
      throw error;
    }
  };
  if (!snapshot)
    return (
      <div className="loading-screen">
        <div className="brand-wordmark">
          <OrniLogo />
          <strong>OrniWork</strong>
        </div>
        <p>{t("app.connecting")}</p>
      </div>
    );
  const pendingCount = snapshot.attention.filter(
    (item) => item.state === "open",
  ).length;
  return (
    <>
      <DesktopSessionShell
        snapshot={snapshot}
        selectedSessionId={selectedSessionId}
        selectedSession={shellSession}
        selectedSessionError={sessionLoadError}
        settingsSection={settingsSection}
        onSettingsSectionChange={setSettingsSection}
        syncStatus={syncStatus}
        sessionContent={({ onOpenSwitcher, onChooseSession, recentSessions, onToggleSessionList, sessionListCollapsed }) => shellSession ? (
          <SessionWorkspace
            session={shellSession}
            snapshot={snapshot}
            client={client}
            insertText={sessionInsertRequest?.sessionId === shellSession.id
              ? { text: sessionInsertRequest.text, nonce: sessionInsertRequest.nonce }
              : null}
            onBack={closeSession}
            onOpenSessionSwitcher={onOpenSwitcher}
            onChooseSession={onChooseSession}
            recentSessions={recentSessions}
            onToggleSessionList={onToggleSessionList}
            sessionListCollapsed={sessionListCollapsed}
            onOpenCreate={() => {
              setNewSessionSeed(null);
              setNewSessionSource("new");
              setNewSessionProvider(null);
              setNewSessionMode("native");
            }}
            onOpenImport={() => {
              setNewSessionSeed(null);
              setNewSessionSource("import");
              setNewSessionProvider(null);
              setNewSessionMode("native");
            }}
            onDelete={() => void client.deleteSession(shellSession.id).then(() => {
              showNotice(t("app.notice.sessionDeleted"));
              closeSession();
              return refresh();
            })}
            providerQuickCommands={providerInventory?.providers.find((provider) =>
              provider.providerId === configurableProviderId(shellSession.provider),
            )?.terminalQuickCommands ?? []}
            onState={(state) =>
              client.setSessionState(shellSession.id, state).catch((error) => {
                showNotice(localizeError(error, t, "app.operationFailed"));
                throw error;
              })
            }
            onRename={(title) =>
              run(
                client.renameSession(shellSession.id, title),
                t("app.notice.sessionRenamed"),
              )
            }
            onNewSessionCurrentDir={(provider, workspacePath) =>
              createSession({
                title: "",
                provider,
                managementMode: "native",
                workspaceRoot: workspacePath,
                workspaceMode: "existing",
                permissionMode: accessPreferences.defaultPermissionMode,
              })
            }
          />
        ) : null}
        initializationContent={
          <DesktopInitializationPanel
            settings={deviceSettings}
            inventory={providerInventory}
            inventoryLoading={providerInventoryLoading}
            onRefreshInventory={refreshInitializationInventory}
            onToggleProvider={toggleInitializationProvider}
            onPickDirectory={() => client.pickDirectoryPath()}
            onSaveComputerName={saveInitializationComputerName}
            onSaveSessionRoot={saveInitializationSessionRoot}
          />
        }
        settingsContent={
          <DesktopSettingsPanel
            snapshot={snapshot}
            client={client}
            inventory={providerInventory}
            onInventoryChange={setProviderInventory}
            onPickDirectory={() => client.pickDirectoryPath()}
            onSessionRootChange={(path) => run(updateSessionRoot(path), t("app.notice.sessionRootSaved"))}
            theme={theme}
            onThemeChange={setTheme}
            onManageApi={(provider) => setApiManagerCli(provider)}
            onOpenHistory={(provider) => {
              setNewSessionSeed(null);
              setNewSessionProvider(provider);
              setNewSessionSource("import");
              setNewSessionMode("native");
              setAllowMultipleImports(true);
            }}
            sessionTransportMode={sessionTransportMode}
            showSessionTransportSetting={client.mode === "local"}
            onSessionTransportModeChange={changeSessionTransportMode}
            accessPreferences={accessPreferences}
            onAccessPreferencesChange={updateAccessPreferences}
            operationStorageScope={client.mode === "cloud" ? cloudTargetDeviceId ?? "cloud-unknown" : "local"}
            syncStatus={syncStatus}
            syncStatusLoading={syncStatusLoading}
            onRefreshSync={() => void loadSyncStatus()}
            settingsSection={settingsSection}
            onSettingsSectionChange={setSettingsSection}
            accountState={authState}
            accountEmail={accountEmail}
            onSignOut={async () => {
              await auth.signOut();
              setAccountEmail(null);
            }}
          />
        }
        onOpenSession={openSession}
        onCloseSession={closeSession}
        onOpenCreate={() => {
          setNewSessionSeed(null);
          setNewSessionSource("new");
          setNewSessionProvider(null);
          setNewSessionMode("native");
        }}
        onOpenImport={() => {
          setNewSessionSeed(null);
          setNewSessionSource("import");
          setNewSessionProvider(null);
          setNewSessionMode("native");
        }}
        onDeleteSession={(id) => client.deleteSession(id).then(() => {
          showNotice(t("app.notice.sessionDeleted"));
          return refresh();
        })}
        initializationRequired={initializationRequired}
        canCompleteInitialization={Boolean(
          deviceSettings &&
          !deviceSettings.onboardingCompleted &&
          configuredNativeAgentCount > 0 &&
          deviceSettings.computerName.trim() &&
          deviceSettings.sessionRoot.trim(),
        )}
        onCompleteInitialization={completeInitialization}
      />
      {false ? (
    <div className="orni-app">
      <aside className="app-rail">
        <button
          className="rail-brand"
          type="button"
          onClick={() => openView("home")}
          aria-label="OrniWork"
        >
          <OrniLogo />
        </button>
        <nav>
          <RailItem
            icon={<DemoRailIcon name="home" />}
            label={t("nav.home")}
            active={view === "home"}
            onClick={() => openView("home")}
          />
          <RecentSessionsRailItem
            sessions={snapshot!.sessions}
            active={view === "sessions"}
            onOpenList={() => openView("sessions")}
            onOpen={openSession}
          />
          <RailItem
            icon={<DemoRailIcon name="task" />}
            label={t("nav.work")}
            active={view === "work"}
            onClick={() => openView("work")}
          />
          <RailItem
            icon={<Bot size={17} strokeWidth={1.6} />}
            label={t("nav.agents")}
            active={view === "agents"}
            onClick={() => openView("agents")}
          />
          <RailItem
            icon={<KeyRound size={17} strokeWidth={1.6} />}
            label={t("nav.api")}
            active={view === "api"}
            onClick={() => openView("api")}
          />
          <NoteRailItem
            client={client}
            active={view === "notes"}
            onOpen={() => openView("notes")}
          />
          {platform.menus.map((item) => (
            <a
              className="rail-item"
              href={item.url}
              target="_blank"
              rel="noreferrer"
              title={item.label}
              aria-label={item.label}
              key={item.id}
            >
              <span><ExternalLink size={17} /></span>
              <span className="rail-tip">{item.label}</span>
            </a>
          ))}
        </nav>
        <div className="rail-bottom">
          <DeviceSwitcherRail
            devices={cloudDevices}
            targetDeviceId={cloudTargetDeviceId}
            onChange={selectCloudTargetDevice}
            // Keep the computer entry point visible as soon as Remote mode is
            // selected. Authentication/device discovery can recover after the
            // shell is painted; hiding the only entry point during that window
            // made a connected local computer look as if the feature vanished.
            visible={client.mode === "cloud" || sessionTransportMode === "remote"}
          />
          <SyncStatusRail
            status={syncStatus}
            loading={syncStatusLoading}
            onRefresh={() => void loadSyncStatus()}
          />
          <LanguageSelector variant="rail" />
          <button
            className="rail-button"
            type="button"
            onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            title={theme === "light" ? t("nav.themeDark") : t("nav.themeLight")}
            aria-label={theme === "light" ? t("nav.themeDark") : t("nav.themeLight")}
          >
            {theme === "light" ? <Moon size={17} strokeWidth={1.6} /> : <Sun size={17} strokeWidth={1.6} />}
          </button>
          <button
            className={`rail-button ${view === "notifications" ? "active" : ""}`}
            type="button"
            onClick={() => openView("notifications")}
            title={t("nav.notifications")}
          >
            <Bell size={17} strokeWidth={1.6} />
            {pendingCount ? <i /> : null}
          </button>
          <button
            className={`rail-button ${view === "help" ? "active" : ""}`}
            type="button"
            onClick={() => openView("help")}
            title={t("nav.help")}
          >
            <CircleHelp size={17} strokeWidth={1.6} />
          </button>
          <button
            className={`rail-button ${view === "settings" ? "active" : ""}`}
            type="button"
            onClick={() => openView("settings")}
            title={t("nav.settings")}
          >
            <DemoRailIcon name="settings" />
          </button>
          <button
            className={`rail-avatar ${authState === "authenticated" ? "authenticated" : ""}`}
            type="button"
            onClick={() => {
              if (authState === "authenticated") setAccountDialogOpen(true);
              else setProductDialog("login");
            }}
            title={authState === "authenticated" ? t("app.account.title") : t("home.signIn")}
            aria-label={authState === "authenticated" ? t("app.account.title") : t("home.signIn")}
          >
            R
          </button>
        </div>
      </aside>
      <main className="app-main">
        <div className="app-content">
          {view === "home" ? (
            <HomePage
              providers={providerInventory?.providers ?? []}
              providersLoading={providerInventoryLoading}
              onCreateWork={createWork}
              onStartSession={(mode, provider) => {
                setNewSessionSeed(null);
                setNewSessionSource("new");
                setNewSessionProvider(provider);
                setNewSessionMode(mode);
              }}
              onImportProject={() => {
                setNewSessionSeed(null);
                setNewSessionSource("import");
                setNewSessionProvider(null);
                setNewSessionMode("native");
              }}
              onOpenWork={() => openView("work")}
              onOpenMobile={() => setProductDialog("mobile")}
              onOpenHelp={() => openView("help")}
              onOpenLogin={() => setProductDialog("login")}
            />
          ) : view === "sessions" && selectedSession ? (
            <SessionWorkspace
              session={selectedSession!}
              snapshot={snapshot!}
              client={client}
              insertText={sessionInsertRequest?.sessionId === selectedSession!.id
                ? { text: sessionInsertRequest!.text, nonce: sessionInsertRequest!.nonce }
                : null}
              onBack={closeSession}
              providerQuickCommands={providerInventory?.providers.find((provider) =>
                provider.providerId === configurableProviderId(selectedSession!.provider),
              )?.terminalQuickCommands ?? []}
              onAutoContinuePolicy={(policy: AutoContinuePolicy) =>
                run(
                  client.setAutoContinuePolicy(selectedSession!.id, policy),
                  t("app.notice.recoverySaved"),
                )
              }
              onState={(state) =>
                client.setSessionState(selectedSession!.id, state).catch((error) => {
                  showNotice(localizeError(error, t, "app.operationFailed"));
                  throw error;
                })
              }
              onRename={(title) =>
                run(
                  client.renameSession(selectedSession!.id, title),
                  t("app.notice.sessionRenamed"),
                )
              }
              onNewSessionCurrentDir={(provider, workspacePath) =>
                createSession({
                  title: "",
                  provider,
                  managementMode: "native",
                  workspaceRoot: workspacePath,
                  workspaceMode: "existing",
                  permissionMode: accessPreferences.defaultPermissionMode,
                })
              }
            />
          ) : view === "sessions" && selectedSessionId && !sessionLoadError ? (
            <div className="session-opening-state" role="status">
              <span className="spinner" />
              <strong>{t("app.session.opening")}</strong>
              <small>{t("app.session.openingDetail")}</small>
            </div>
          ) : view === "sessions" && selectedSessionId && sessionLoadError ? (
            <div
              className="session-opening-state session-opening-error"
              role="alert"
            >
              <strong>{t("app.session.openFailed")}</strong>
              <small>{sessionLoadError}</small>
              <button
                className="ghost-button"
                type="button"
                onClick={closeSession}
              >
                {t("app.session.backToWorkbench")}
              </button>
            </div>
          ) : view === "sessions" ? (
            <SessionsPage
              snapshot={snapshot!}
              providers={providerInventory?.providers ?? []}
              providersLoading={providerInventoryLoading}
              onOpen={openSession}
              onStartSession={(mode, provider) => {
                setNewSessionSeed(null);
                setNewSessionSource("new");
                setNewSessionProvider(provider);
                setNewSessionMode(mode);
              }}
              onImportProject={() => {
                setNewSessionSeed(null);
                setNewSessionSource("import");
                setNewSessionProvider(null);
                setNewSessionMode("native");
              }}
              onOpenWork={() => openView("work")}
              onStart={(id) =>
                run(client.startSession(id), t("app.notice.sessionStarted"))
              }
              onStop={(id) =>
                run(client.stopSession(id), t("app.notice.sessionStopped"))
              }
              onStopAll={() =>
                run(
                  client.stopAllSessions().then(() => refresh()),
                  t("app.notice.allSessionsStopped"),
                )
              }
              onRestart={(id) =>
                run(client.restartSession(id), t("app.notice.sessionRestarted"))
              }
              onDelete={(id) =>
                run(client.deleteSession(id), t("app.notice.sessionDeleted"))
              }
            />
          ) : view === "work" ? (
            <WorkPage />
          ) : view === "notes" ? (
            <NotesPage
              key={`notes:${authState}`}
              client={client}
              sessions={snapshot!.sessions}
              onOpenSession={(sessionId, insertText) => openSession(sessionId, undefined, insertText)}
              onCreateSession={(note, title) => {
                setNewSessionSeed({ title, noteId: note.id });
                setNewSessionSource("new");
                setNewSessionProvider(null);
                setNewSessionMode("native");
              }}
            />
          ) : view === "notifications" ? (
            <NotificationsPage
              snapshot={snapshot!}
              onOpenSession={openSession}
            />
          ) : view === "help" ? (
            <HelpPage />
          ) : view === "agents" ? (
            <AgentManagementPage
              snapshot={snapshot!}
              client={client}
              inventory={providerInventory}
              onInventoryChange={setProviderInventory}
              onPickDirectory={() => client.pickDirectoryPath()}
              onSessionRootChange={(path) =>
                run(updateSessionRoot(path), t("app.notice.sessionRootSaved"))
              }
              theme={theme}
              onThemeChange={setTheme}
              onManageApi={(provider) => {
                setApiManagerCli(provider);
                openView("api");
              }}
              onOpenHistory={(provider) => {
                setNewSessionSeed(null);
                setNewSessionProvider(provider);
                setNewSessionSource("import");
                setNewSessionMode("native");
                setAllowMultipleImports(true);
              }}
              sessionTransportMode={sessionTransportMode}
              showSessionTransportSetting={false}
              onSessionTransportModeChange={changeSessionTransportMode}
              accessPreferences={accessPreferences}
              onAccessPreferencesChange={updateAccessPreferences}
              operationStorageScope={client.mode === "cloud" ? cloudTargetDeviceId ?? "cloud-unknown" : "local"}
            />
          ) : view === "api" ? (
            <ApiManagerPage client={client} initialCli={apiManagerCli} />
          ) : (
            <ProviderSettingsPage
              snapshot={snapshot!}
              client={client}
              inventory={providerInventory}
              onInventoryChange={setProviderInventory}
              onPickDirectory={() => client.pickDirectoryPath()}
              onSessionRootChange={(path) =>
                run(updateSessionRoot(path), t("app.notice.sessionRootSaved"))
              }
              theme={theme}
              onThemeChange={setTheme}
              sessionTransportMode={sessionTransportMode}
              showSessionTransportSetting={client.mode === "local"}
              onSessionTransportModeChange={changeSessionTransportMode}
              accessPreferences={accessPreferences}
              onAccessPreferencesChange={updateAccessPreferences}
              operationStorageScope={client.mode === "cloud" ? cloudTargetDeviceId ?? "cloud-unknown" : "local"}
            />
          )}
        </div>
      </main>
    </div>
      ) : null}
      {newSessionMode ? (
        <SessionCreateDialog
          key={`${newSessionSource}:${newSessionProvider ?? "all"}:${newSessionSeed?.title ?? ""}`}
          initialMode={newSessionMode}
          initialProvider={newSessionProvider ?? undefined}
          enabledProviders={(providerInventory?.providers ?? [])
            .filter((provider) => provider.supportsNative && (provider.enabled || provider.installed))
            .map((provider) => provider.providerId)}
          providerDisplayNames={Object.fromEntries(
            (providerInventory?.providers ?? [])
              .filter((provider) => provider.supportsNative && (provider.enabled || provider.installed))
              .map((provider) => [provider.providerId, provider.displayName]),
          ) as Partial<Record<ConfigurableProviderId, string>>}
          source={newSessionSource}
          allowMultipleImports={allowMultipleImports}
          historyProviderLocked={false}
          initialTitle={newSessionSeed?.title}
          sessionRoot={deviceSettings?.sessionRoot ?? snapshot.preferences.sessionRoot}
          initialPermissionMode={accessPreferences.defaultPermissionMode}
          onClose={() => {
            setNewSessionMode(null);
            setNewSessionProvider(null);
            setNewSessionSource("new");
            setNewSessionSeed(null);
            setAllowMultipleImports(false);
          }}
          onOpenImport={() => {
            setNewSessionProvider(null);
            setNewSessionSource("import");
            setAllowMultipleImports(false);
          }}
          onCreate={createSession}
          onPickDirectory={() => client.pickDirectoryPath()}
          onDiscoverProviderHistory={(provider, options) =>
            emptyInitPreview
              ? Promise.resolve(emptyPreviewHistoryCatalog())
              : client.discoverProviderHistory([], provider, options)
          }
          onImportProviderHistory={async (input, importedSessionId) => {
            if (emptyInitPreview) return;
            // Daemon imports are intentionally bounded per command so a
            // large Codex catalog cannot monopolize one SQLite transaction or
            // exceed the Cloud command body/receipt deadline.  Keep the UI
            // operation as one logical import while sending bounded chunks.
            // This also makes “select all” work for installations with
            // thousands of historical conversations.
            const chunkSize = 200;
            let importedId = importedSessionId;
            let openedImportedSession = Boolean(importedSessionId);
            for (let offset = 0; offset < input.length; offset += chunkSize) {
              const result = await client.importProviderHistory(
                input.slice(offset, offset + chunkSize),
              );
              importedId ??= result.imports[0]?.session.id;
              // Enter the first durable imported Session as soon as the
              // Daemon accepts a chunk. Remaining chunks continue in the
              // background, so a large history import never strands the user
              // behind the picker until every page has finished.
              if (!openedImportedSession && importedId) {
                openedImportedSession = true;
                openSession(importedId, "terminal");
              }
              // Keep the session list current between chunks so another
              // client can observe imports as they commit, without waiting
              // for the full catalog.
              setSnapshot(await client.snapshot());
            }
            if (importedId && !openedImportedSession) openSession(importedId, "terminal");
          }}
          onImportStatus={(status) => {
            const messageKey = status.state === "accepted"
              ? "app.historyImport.accepted"
              : status.state === "completed"
                ? "app.historyImport.completed"
                : "app.historyImport.failed";
            showNotice(t(messageKey, { count: status.count, provider: status.provider }));
          }}
          onOpenImportedSession={(sessionId) => {
            setNewSessionMode(null);
            setNewSessionProvider(null);
            setNewSessionSource("new");
            openSession(sessionId, "terminal");
          }}
        />
      ) : null}
      {!newSessionMode &&
      !productDialog &&
      !recoveryDialogDeferred &&
      recoveryDialogEnabled &&
      recoveryIncidents.length ? (
        <RecoveryDialog
          incidents={recoveryIncidents}
          onClose={() => setRecoveryDialogDeferred(true)}
          onOpenSession={openSession}
          onResolve={async (incidentId, action) => {
            await client.resolveRecoveryIncident(incidentId, action);
            setRecoveryIncidents((current) =>
              current.filter((incident) => incident.incidentId !== incidentId),
            );
            void refresh();
          }}
        />
      ) : null}
      <PlatformNotices
        update={platform.update}
        announcements={platform.announcements}
      />
      {productDialog ? (
        <ProductDialog
          kind={productDialog}
          auth={auth}
          authState={authState}
          onAuthenticated={() => {
            setNotice(t("app.auth.signedIn"));
            setProductDialog(null);
          }}
          onClose={() => setProductDialog(null)}
        />
      ) : null}
      {accountDialogOpen && authState === "authenticated" ? (
        <AccountDialog
          auth={auth}
          onClose={() => setAccountDialogOpen(false)}
          onSignOut={() => auth.signOut()}
        />
      ) : null}
      {notice ? (
        <div className="toast">
          <Zap size={14} />
          {notice}
        </div>
      ) : null}
    </>
  );
}

function isView(value: string | null): value is View {
  return (
    value === "home" ||
    value === "sessions" ||
    value === "work" ||
    value === "notes" ||
    value === "agents" ||
    value === "api" ||
    value === "notifications" ||
    value === "help" ||
    value === "settings"
  );
}

/** Skills and retired onboarding links return to the current product home. */
export function desktopViewFromRoute(value: string | null): View {
  if (["skills", "onboarding", "setup"].includes(value ?? "")) return "home";
  return isView(value) ? value : "home";
}

function emptyInitializationPreviewSnapshot(): Snapshot {
  return {
    sessions: [],
    attention: [],
    daemon: { state: "online", version: "Preview", latency: 0 },
    preferences: { sessionRoot: "~/Orni" },
  };
}

function NoteRailItem({
  client,
  active,
  onOpen,
}: {
  client: DesktopClient;
  active: boolean;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const openPopover = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    setPopoverOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setPopoverOpen(false);
      closeTimer.current = null;
    }, 240);
  };
  useEffect(() => () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
  }, []);
  return (
    <div
      className={`rail-note-wrap ${popoverOpen ? "open" : ""}`}
      onMouseEnter={openPopover}
      onMouseLeave={scheduleClose}
      onFocus={openPopover}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) scheduleClose();
      }}
    >
      <button
        className={`rail-item ${active ? "active" : ""}`}
        type="button"
        onClick={onOpen}
        aria-label={t("nav.notes")}
      >
        <span><Lightbulb size={18} /></span>
      </button>
      <QuickNotePopover client={client} />
    </div>
  );
}

function snapshotRefreshDelay(event: OrniEvent): number | null {
  switch (event.type) {
    case "terminal.appended":
      return null;
    case "session.created":
    case "session.deleted":
      return 80;
    case "notification.created":
    case "notification.updated":
      return 350;
    case "session.updated":
      return 550;
    case "session.detail.invalidated":
      return 1_200;
    case "daemon.updated":
      return 1_500;
    case "snapshot.invalidated":
      return 900;
  }
}

function detailRefreshDelay(event: OrniEvent): number {
  switch (event.type) {
    case "session.detail.invalidated":
      return 220;
    case "session.updated":
      return 450;
    case "terminal.appended":
      return 850;
    default:
      return 700;
  }
}

function defaultSessionMode(
  _session: Session | undefined,
  _clientMode: DesktopClient["mode"],
): SessionMode {
  return "terminal";
}

function configurableProviderId(provider: Session["provider"]): "codex" | "claude" | "opencode" {
  if (provider === "Claude Code") return "claude";
  if (provider === "OpenCode") return "opencode";
  return "codex";
}

function updateBrowserRoute(
  route:
    | { view: View; sessionId?: never; mode?: never }
    | { sessionId: string; mode: SessionMode; view?: never },
  method: "push" | "replace",
) {
  const params = new URLSearchParams();
  if (route.sessionId && route.mode) {
    params.set("session", route.sessionId);
    params.set("mode", route.mode);
  } else if (route.view && route.view !== "home") {
    params.set("view", route.view);
  }
  const search = params.toString();
  window.history[method === "push" ? "pushState" : "replaceState"](
    null,
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}`,
  );
}

function ProductDialog({
  kind,
  auth,
  authState,
  onAuthenticated,
  onClose,
}: {
  kind: "mobile" | "login";
  auth: AuthPort;
  authState: AuthState;
  onAuthenticated: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const mobile = kind === "mobile";
  return (
    <div
      className="dialog-backdrop product-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={mobile ? t("app.product.mobileAria") : t("app.product.signInAria")}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="product-dialog">
        <header>
          <span className="product-dialog-icon">
            {mobile ? <Smartphone size={19} /> : <LogIn size={19} />}
          </span>
          <div>
            <h2>{mobile ? t("app.product.mobileTitle") : t("app.product.signInTitle")}</h2>
            <p>
              {mobile
                ? t("app.product.mobileLead")
                : t("app.product.signInLead")}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={t("common.close")}>
            <X size={16} />
          </button>
        </header>
        {mobile ? (
          <div className="mobile-download-body">
            <div className="mobile-qr">
              <QRCodeSVG
                value="https://orni.app/mobile"
                size={132}
                bgColor="#ffffff"
                fgColor="#111318"
                level="M"
              />
            </div>
            <div>
              <strong>{t("app.product.mobileQrTitle")}</strong>
              <p>{t("app.product.mobilePending")}</p>
              <code>orni.app/mobile</code>
            </div>
          </div>
        ) : (
          <div className="login-dialog-body">
            {authState === "authenticated" ? (
              <div className="desktop-login-authenticated">
                <span><ShieldCheck size={18} /></span>
                <div>
                  <strong>{t("app.auth.signedInTitle")}</strong>
                  <small>{t("app.auth.signedInDetail")}</small>
                </div>
                <button type="button" onClick={() => void auth.signOut()}>
                  {t("app.auth.signOut")}
                </button>
              </div>
            ) : authState === "unconfigured" ? (
              <div className="desktop-login-unavailable" role="status">
                <UserRound size={18} />
                <span>
                  <strong>{t("app.product.accountTitle")}</strong>
                  <small>{t("app.auth.notConfigured")}</small>
                </span>
              </div>
            ) : (
              <DesktopLoginForm auth={auth} onSuccess={onAuthenticated} />
            )}
            <p>{t("app.product.localWithoutSignIn")}</p>
          </div>
        )}
      </section>
    </div>
  );
}

function DeviceSwitcherRail({
  devices,
  targetDeviceId,
  onChange,
  visible,
}: {
  devices: RemoteDeviceSummary[];
  targetDeviceId: string | null;
  onChange: (deviceId: string | null) => void;
  visible: boolean;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = devices.find((device) => device.deviceId === targetDeviceId)
    ?? devices.find((device) => device.online)
    ?? devices[0];

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (!visible) return null;
  return (
    <div className={`rail-device-switcher ${open ? "open" : ""}`} ref={rootRef}>
      <button
        className={`rail-button ${open ? "active" : ""}`}
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={selected?.displayName ?? t("sessions.chooseComputer")}
        aria-label={t("sessions.computer")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MonitorSmartphone size={17} strokeWidth={1.6} />
        <i className={selected?.online ? "online" : "offline"} />
      </button>
      {open ? (
        <div className="rail-device-menu" role="menu" aria-label={t("sessions.chooseComputer")}>
          <header>
            <strong>{t("sessions.chooseComputer")}</strong>
            <small>{t("sessions.computerTargetHint")}</small>
          </header>
          <div>
            {devices.length ? devices.map((device) => (
              <button
                className={device.deviceId === selected?.deviceId ? "active" : ""}
                type="button"
                role="menuitemradio"
                aria-checked={device.deviceId === selected?.deviceId}
                key={device.deviceId}
                onClick={() => {
                  onChange(device.deviceId);
                  setOpen(false);
                }}
              >
                <span className={`device-status-dot ${device.online ? "online" : "offline"}`} />
                <span>
                  <strong>{device.displayName}</strong>
                  <small>{t(device.online ? "settings.service.online" : "settings.service.offline")}</small>
                </span>
                {device.deviceId === selected?.deviceId ? <Check size={14} /> : null}
              </button>
            )) : (
              <div className="rail-device-empty" role="status">
                {t("sessions.computerUnavailable")}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function deviceListFingerprint(devices: RemoteDeviceSummary[]): string {
  return devices
    .map((device) => [
      device.deviceId,
      device.displayName,
      device.online ? "1" : "0",
      device.lastSeenAt ?? "",
      device.protocolMin ?? "",
      device.protocolMax ?? "",
    ].join(":"))
    .join("|");
}

function SyncStatusRail({
  status,
  loading,
  onRefresh,
}: {
  status: SyncStatus | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const state = status?.state ?? "offline";
  const label = loading && !status
    ? t("sync.loading")
    : state === "current"
      ? t("sync.current")
      : state === "pending"
        ? t("sync.pending")
        : state === "error"
          ? t("sync.error")
          : t("sync.offline");
  const pending = status?.streams.reduce((sum, stream) => sum + stream.pendingCount, 0) ?? 0;
  const failed = status?.streams.reduce((sum, stream) => sum + stream.failedCount, 0) ?? 0;
  return (
    <div className={`rail-sync-status ${open ? "open" : ""}`} ref={rootRef}>
      <button
        className={`rail-button sync-${state}`}
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={label}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Wifi size={17} strokeWidth={1.6} />
        <i />
      </button>
      {open ? (
        <div className="rail-sync-menu" role="dialog" aria-label={t("sync.title")}>
          <header>
            <span>
              <strong>{t("sync.title")}</strong>
              <small>{label}</small>
            </span>
            <button type="button" onClick={onRefresh} title={t("sync.refresh")} aria-label={t("sync.refresh")} disabled={loading}>
              <RefreshCw size={13} className={loading ? "spin" : ""} />
            </button>
          </header>
          {status ? (
            <div className="rail-sync-summary">
              <span>{pending ? t("sync.pendingDetail", { count: pending }) : t("sync.current")}</span>
              {failed ? <span className="error">{t("sync.failedDetail", { count: failed })}</span> : null}
            </div>
          ) : (
            <p className="rail-sync-empty">{t("sync.loading")}</p>
          )}
          {status?.streams.length ? (
            <div className="rail-sync-streams">
              {status.streams.map((stream) => (
                <div key={stream.stream}>
                  <span>{stream.stream}</span>
                  <small>{stream.pendingCount} pending · {stream.failedCount} failed</small>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RailItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`rail-item ${active ? "active" : ""}`}
      type="button"
      onClick={onClick}
      aria-label={label}
    >
      <span>{icon}</span>
    </button>
  );
}
function RecentSessionsRailItem({
  sessions,
  active,
  onOpenList,
  onOpen,
}: {
  sessions: Session[];
  active: boolean;
  onOpenList: () => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useI18n();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const openPopover = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    setPopoverOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setPopoverOpen(false);
      closeTimer.current = null;
    }, 200);
  };
  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );
  const recent = [...sessions]
    .sort(
      (left, right) =>
        Date.parse(right.updatedAt ?? "") - Date.parse(left.updatedAt ?? ""),
    )
    .slice(0, 6);
  return (
    <div
      className={`rail-recent-wrap ${popoverOpen ? "open" : ""}`}
      onMouseEnter={openPopover}
      onMouseLeave={scheduleClose}
      onFocus={openPopover}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) scheduleClose();
      }}
    >
      <button
        className={`rail-item ${active ? "active" : ""}`}
        type="button"
        onClick={onOpenList}
        aria-label={t("nav.sessions")}
      >
        <span>
          <DemoRailIcon name="session" />
        </span>
      </button>
      <div className="rail-recent-popover" role="menu" aria-label={t("app.recent.title")}>
        <header>
          <strong>{t("app.recent.title")}</strong>
          <button type="button" onClick={onOpenList}>
            {t("app.recent.viewAll")}
          </button>
        </header>
        {recent.length ? (
          recent.map((session) => (
            <button
              className="rail-recent-session"
              type="button"
              role="menuitem"
              key={session.id}
              onClick={() => onOpen(session.id)}
            >
              <i className={`rail-recent-dot rail-recent-${session.state}`} />
              <span>
                <strong>{session.title}</strong>
                <small>
                  {session.provider} · {session.updated}
                </small>
              </span>
            </button>
          ))
        ) : (
          <p>{t("app.recent.empty")}</p>
        )}
      </div>
    </div>
  );
}
function OrniLogo() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3.2" fill="currentColor" />
      <ellipse
        cx="12"
        cy="12"
        rx="10.2"
        ry="4.3"
        stroke="currentColor"
        strokeWidth="1.3"
        opacity=".75"
      />
      <ellipse
        cx="12"
        cy="12"
        rx="10.2"
        ry="4.3"
        stroke="currentColor"
        strokeWidth="1.3"
        opacity=".4"
        transform="rotate(60 12 12)"
      />
      <ellipse
        cx="12"
        cy="12"
        rx="10.2"
        ry="4.3"
        stroke="currentColor"
        strokeWidth="1.3"
        opacity=".4"
        transform="rotate(120 12 12)"
      />
    </svg>
  );
}
function DemoRailIcon({
  name,
}: {
  name: "home" | "session" | "task" | "settings";
}) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  if (name === "home")
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <path d="M4 11.5 12 4l8 7.5" />
        <path d="M6 10v9a1 1 0 0 0 1 1h3.5v-6h3v6H17a1 1 0 0 0 1-1v-9" />
      </svg>
    );
  if (name === "session")
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
        <path d="M7.5 9.5 10.5 12l-3 2.5M12.5 14.5h4" />
      </svg>
    );
  if (name === "task")
    return (
      <svg viewBox="0 0 24 24" {...common}>
        <rect x="4" y="4.5" width="16" height="15" rx="2.5" />
        <path d="M8 10.5 9.8 12.3 13.5 8.6M8 15.5h8" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" {...common}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 13a7.9 7.9 0 0 0 0-2l2-1.5-2-3.4-2.4 1a8 8 0 0 0-1.7-1l-.4-2.5H9.1l-.4 2.5a8 8 0 0 0-1.7 1l-2.4-1-2 3.4L4.6 11a7.9 7.9 0 0 0 0 2l-2 1.5 2 3.4 2.4-1c.5.4 1.1.75 1.7 1l.4 2.5h5.8l.4-2.5a8 8 0 0 0 1.7-1l2.4 1 2-3.4-2-1.5Z" />
    </svg>
  );
}
function SettingsPage({
  snapshot,
  clientMode,
  onPickDirectory,
  onSessionRootChange,
}: {
  snapshot: Snapshot;
  clientMode: DesktopClient["mode"];
  onPickDirectory: () => Promise<string | null>;
  onSessionRootChange: (path: string) => void;
}) {
  const [terminalPrimary, setTerminalPrimary] = useState(true);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [remoteEnabled, setRemoteEnabled] = useState(false);
  const [installProvider, setInstallProvider] = useState<{
    name: string;
    command: string;
  } | null>(null);
  const [sessionRoot, setSessionRoot] = useState(
    snapshot.preferences.sessionRoot,
  );
  useEffect(
    () => setSessionRoot(snapshot.preferences.sessionRoot),
    [snapshot.preferences.sessionRoot],
  );
  const chooseSessionRoot = async () => {
    const path = await onPickDirectory();
    if (path) setSessionRoot(path);
  };
  const saveSessionRoot = () => {
    const value = sessionRoot.trim().replace(/\/$/, "");
    if (value) onSessionRootChange(value);
  };
  const adapters = [
    {
      name: "Codex",
      detail: "App Server / PTY",
      ready: true,
      command: "npm install -g @openai/codex",
    },
    {
      name: "Claude Code",
      detail: "JSONL / PTY",
      ready: true,
      command: "npm install -g @anthropic-ai/claude-code",
    },
    {
      name: "OpenCode",
      detail: "PTY only",
      ready: false,
      command: "npm install -g opencode-ai",
    },
  ];
  return (
    <div className="settings-page page-scroll">
      <header className="list-page-header">
        <div>
          <h1>设置</h1>
          <p>管理本地服务、Provider、会话目录和远程访问。</p>
        </div>
      </header>
      <div className="settings-grid">
        <section>
          <h2>本地服务</h2>
          <div className="settings-row">
            <span>
              <strong>本地服务</strong>
              <small>{snapshot.daemon.version}</small>
            </span>
            <b
              className={
                snapshot.daemon.state === "online"
                  ? "setting-online"
                  : "setting-offline"
              }
            >
              {snapshot.daemon.state === "online"
                ? `在线 · ${snapshot.daemon.latency}ms`
                : "离线"}
            </b>
          </div>
          <div className="settings-row">
            <span>
              <strong>控制 API</strong>
              <small>
                Loopback HTTP + WebSocket ·{" "}
                {clientMode === "local" ? "Local transport" : "Demo transport"}
              </small>
            </span>
            <button
              className="ghost-button"
              type="button"
              onClick={() => setConnectionOpen((value) => !value)}
            >
              <Wifi size={13} />
              {connectionOpen ? "收起连接" : "查看连接"}
            </button>
          </div>
          {connectionOpen ? (
            <div className="connection-detail">
              <code>
                {clientMode === "local"
                  ? "/orni-api → 127.0.0.1"
                  : "Demo client"}
              </code>
              <span>Desktop 与本地服务使用同一会话注册表</span>
            </div>
          ) : null}
        </section>
        <section>
          <h2>会话目录</h2>
          <div className="session-root-setting">
            <label>
              <span>全局会话根目录</span>
              <input
                value={sessionRoot}
                onChange={(event) => setSessionRoot(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") saveSessionRoot();
                }}
              />
            </label>
            <button
              className="ghost-button"
              type="button"
              onClick={() => void chooseSessionRoot()}
            >
              <FolderOpen size={13} />
              选择目录
            </button>
            <button
              className="primary-button small"
              type="button"
              onClick={saveSessionRoot}
            >
              <Save size={13} />
              保存
            </button>
          </div>
          <p className="setting-note">
            新会话默认创建在这个目录下，并使用会话 ID 作为子目录。
          </p>
        </section>
        <section>
          <h2>账户与远程</h2>
          <div className="settings-row">
            <span>
              <strong>Orni 账户</strong>
              <small>未登录也可以使用本地会话</small>
            </span>
            <button className="ghost-button" type="button">
              <UserRound size={13} />
              登录
            </button>
          </div>
          <div className="settings-row">
            <span>
              <strong>远程控制</strong>
              <small>手机和其他设备通过 Public API 访问</small>
            </span>
            <button
              type="button"
              className={`toggle ${remoteEnabled ? "on" : ""}`}
              aria-label="远程控制"
              onClick={() => setRemoteEnabled((value) => !value)}
            >
              <i />
            </button>
          </div>
          <div className="settings-row">
            <span>
              <strong>本地密钥</strong>
              <small>按设备保存，不展示给页面</small>
            </span>
            <button className="ghost-button" type="button">
              <KeyRound size={13} />
              管理
            </button>
          </div>
        </section>
        <section>
          <h2>默认会话</h2>
          <div className="settings-row">
            <span>
              <strong>创建入口</strong>
              <small>默认推荐 Orni Work，可在创建时切换为 Native</small>
            </span>
            <span className="adapter-state ready">Orni Work</span>
          </div>
          <div className="settings-row">
            <span>
              <strong>原生终端为主</strong>
              <small>所有会话始终保留完整 Terminal 视图</small>
            </span>
            <button
              type="button"
              className={`toggle ${terminalPrimary ? "on" : ""}`}
              aria-label="原生终端为主"
              onClick={() => setTerminalPrimary((value) => !value)}
            >
              <i />
            </button>
          </div>
        </section>
        <section>
          <h2>Provider Adapter</h2>
          {adapters.map((adapter) => (
            <div className="settings-row" key={adapter.name}>
              <span>
                <strong>
                  {adapter.name} · {adapter.detail}
                </strong>
                <small>
                  {adapter.ready
                    ? "部分结构化事件已验证"
                    : "尚未安装或未检测到"}
                </small>
              </span>
              {adapter.ready ? (
                <span className="adapter-state ready">Ready</span>
              ) : (
                <button
                  className="adapter-install-button"
                  type="button"
                  onClick={() =>
                    setInstallProvider({
                      name: adapter.name,
                      command: adapter.command,
                    })
                  }
                >
                  安装
                </button>
              )}
            </div>
          ))}
        </section>
        <section>
          <h2>隐私与安全</h2>
          <div className="settings-row">
            <span>
              <strong>原始终端保留</strong>
              <small>优先保留最近窗口，历史按需加载</small>
            </span>
            <span className="setting-online">
              <ShieldCheck size={14} />
              本地
            </span>
          </div>
          <div className="settings-row">
            <span>
              <strong>分析降级</strong>
              <small>旁路能力失败时不阻塞原生输入输出</small>
            </span>
            <span className="setting-online">
              <Cloud size={14} />
              旁路
            </span>
          </div>
        </section>
      </div>
      {installProvider ? (
        <CommandDialog
          title={`安装 ${installProvider.name} Provider Adapter`}
          description="先安装 Provider CLI，Orni 会在下次检测时启用对应 Adapter。"
          command={installProvider.command}
          onClose={() => setInstallProvider(null)}
        />
      ) : null}
    </div>
  );
}
